// src/components/admin/payroll/hooks/useWorkingDays.js
// Custom hook for calculating working days, holidays, and weekly offs

import { useState, useEffect, useCallback, useMemo } from 'react';
import payrollService from '../../../../services/payroll.service';
import { toISTDateString, getISTComponents } from '../../../../utils/dateHelpers';

const useWorkingDays = (period, companyId = null) => {
    const [holidays, setHolidays] = useState([]);
    const [weeklyOffPolicy, setWeeklyOffPolicy] = useState('sunday_only'); // sunday_only, saturday_sunday, custom
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Parse period string to get month and year
    const { month, year } = useMemo(() => {
        if (!period) return { month: new Date().getMonth(), year: new Date().getFullYear() };

        // Handle formats: "NOV-2025", "November 2025", "2025-11"
        const periodStr = period.toString().toUpperCase();

        // Format: "NOV-2025" or "NOVEMBER-2025"
        const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const fullMonthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];

        for (let i = 0; i < monthNames.length; i++) {
            if (periodStr.includes(monthNames[i]) || periodStr.includes(fullMonthNames[i])) {
                const yearMatch = periodStr.match(/\d{4}/);
                return { month: i, year: yearMatch ? parseInt(yearMatch[0]) : new Date().getFullYear() };
            }
        }

        // Format: "2025-11"
        const isoMatch = periodStr.match(/(\d{4})-(\d{1,2})/);
        if (isoMatch) {
            return { month: parseInt(isoMatch[2]) - 1, year: parseInt(isoMatch[1]) };
        }

        return { month: new Date().getMonth(), year: new Date().getFullYear() };
    }, [period]);

    // Fetch holidays for the period
    const fetchHolidays = useCallback(async () => {
        if (!period) return;

        setLoading(true);
        setError(null);

        try {
            const result = await payrollService.getHolidays(year, month + 1);
            if (result.success) {
                // FIX: Use toISTDateString for timezone-safe date conversion
                const holidayDates = (result.holidays || []).map(h => {
                    return toISTDateString(h.date);
                });
                setHolidays(holidayDates);
            }
        } catch (err) {
            console.error('Error fetching holidays:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [period, year, month]);

    // Fetch weekly off policy
    const fetchWeeklyOffPolicy = useCallback(async () => {
        try {
            // This would be fetched from company settings
            // For now, default to sunday_only
            setWeeklyOffPolicy('sunday_only');
        } catch (err) {
            console.error('Error fetching weekly off policy:', err);
        }
    }, []);

    useEffect(() => {
        fetchHolidays();
        fetchWeeklyOffPolicy();
    }, [fetchHolidays, fetchWeeklyOffPolicy]);

    // Get weekly off days based on policy
    const getWeeklyOffDays = useCallback((policy = weeklyOffPolicy) => {
        switch (policy) {
            case 'saturday_sunday':
                return [0, 6]; // Sunday = 0, Saturday = 6
            case 'sunday_only':
            default:
                return [0]; // Sunday only
        }
    }, [weeklyOffPolicy]);

    // Calculate payable days for the month
    // NOTE: Payable days = ALL calendar days (holidays and weekly offs are PAID)
    // holidayCount and weeklyOffCount are returned for LOP calculation
    const calculateWorkingDays = useCallback((
        customMonth = month,
        customYear = year,
        startDate = null,
        endDate = null,
        customHolidays = holidays,
        customWeeklyOffs = null
    ) => {
        const monthStart = startDate || new Date(customYear, customMonth, 1);
        const monthEnd = endDate || new Date(customYear, customMonth + 1, 0);

        const weeklyOffs = customWeeklyOffs || getWeeklyOffDays();

        let holidayCount = 0;
        let weeklyOffCount = 0;
        let totalDays = 0;

        const currentDate = new Date(monthStart);
        while (currentDate <= monthEnd) {
            totalDays++;
            // FIX: Use getISTComponents for timezone-safe day-of-week and toISTDateString for date
            const { dayOfWeek } = getISTComponents(currentDate);
            const dateStr = toISTDateString(currentDate);

            // Check if it's a weekly off
            if (weeklyOffs.includes(dayOfWeek)) {
                weeklyOffCount++;
            } else if (customHolidays.includes(dateStr)) {
                // Check if it's a holiday (only count if not already a weekly off)
                holidayCount++;
            }

            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Payable days = ALL calendar days (holidays and weekly offs are PAID)
        const workingDays = totalDays;

        return {
            totalDays,
            workingDays,
            holidayCount,
            weeklyOffCount,
            month: customMonth,
            year: customYear
        };
    }, [month, year, holidays, getWeeklyOffDays]);

    // Calculate payable days between two dates (for pro-rata)
    // NOTE: Returns total calendar days (holidays and weekly offs are PAID)
    // Also returns holidayCount and weeklyOffCount for LOP calculation
    const calculateWorkingDaysBetween = useCallback((startDate, endDate, customHolidays = holidays) => {
        if (!startDate || !endDate) return { totalDays: 0, holidayCount: 0, weeklyOffCount: 0 };

        const start = new Date(startDate);
        const end = new Date(endDate);
        const weeklyOffs = getWeeklyOffDays();

        let totalDays = 0;
        let holidayCount = 0;
        let weeklyOffCount = 0;
        const currentDate = new Date(start);

        while (currentDate <= end) {
            totalDays++;
            // FIX: Use getISTComponents for timezone-safe day-of-week and toISTDateString for date
            const { dayOfWeek } = getISTComponents(currentDate);
            const dateStr = toISTDateString(currentDate);

            // Count weekly offs and holidays
            if (weeklyOffs.includes(dayOfWeek)) {
                weeklyOffCount++;
            } else if (customHolidays.includes(dateStr)) {
                holidayCount++;
            }

            currentDate.setDate(currentDate.getDate() + 1);
        }

        // Payable days = ALL calendar days
        return { totalDays, holidayCount, weeklyOffCount };
    }, [holidays, getWeeklyOffDays]);

    // Calculate LOP (Loss of Pay)
    // Formula: LOP = Payable Days - Present Days - Approved Leaves - Holidays - Weekly Offs
    // NOTE: Payable Days = ALL calendar days (holidays and weekly offs are PAID)
    const calculateLOP = useCallback((employee, workingDaysData = null) => {
        const data = workingDaysData || calculateWorkingDays();
        const payableDays = data.workingDays; // Now equals total calendar days
        const holidayCount = data.holidayCount || 0;
        const weeklyOffCount = data.weeklyOffCount || 0;

        const presentDays = employee.present_days || employee.presentDays || 0;
        const approvedLeaves = employee.approved_leaves || employee.approvedLeaves || 0;

        // LOP = Payable Days - Present Days - Approved Leaves - Holidays - Weekly Offs
        const lopDays = Math.max(0, payableDays - presentDays - approvedLeaves - holidayCount - weeklyOffCount);

        // Calculate LOP deduction based on basic + DA
        const basicSalary = employee.basic_salary || employee.basicSalary || 0;
        const da = employee.da || employee.dearness_allowance || 0;
        const basicPlusDA = basicSalary + da;

        const perDayRate = payableDays > 0 ? basicPlusDA / payableDays : 0;
        const lopDeduction = Math.round(perDayRate * lopDays);

        return {
            lopDays,
            lopDeduction,
            perDayRate: Math.round(perDayRate),
            payableDays,
            presentDays,
            approvedLeaves,
            holidayCount,
            weeklyOffCount,
            effectivePayableDays: payableDays - lopDays
        };
    }, [calculateWorkingDays]);

    // Calculate pro-rata for new joiners/exits
    // NOTE: Pro-rata is based on total calendar days (holidays and weekly offs are PAID)
    const calculateProRata = useCallback((employee, workingDaysData = null) => {
        const data = workingDaysData || calculateWorkingDays();
        const totalPayableDays = data.workingDays; // Now equals total calendar days

        const monthStart = new Date(year, month, 1);
        const monthEnd = new Date(year, month + 1, 0);

        // Get joining and exit dates
        const joiningDate = employee.joining_date || employee.joiningDate || employee.date_of_joining
            ? new Date(employee.joining_date || employee.joiningDate || employee.date_of_joining)
            : null;

        const exitDate = employee.exit_date || employee.exitDate || employee.last_working_day
            ? new Date(employee.exit_date || employee.exitDate || employee.last_working_day)
            : null;

        let effectiveStart = monthStart;
        let effectiveEnd = monthEnd;
        let isProRated = false;
        let joinerType = null;

        // Check for mid-month joining
        if (joiningDate && joiningDate.getMonth() === month && joiningDate.getFullYear() === year) {
            effectiveStart = joiningDate;
            isProRated = true;
            joinerType = 'joiner';
        }

        // Check for mid-month exit
        if (exitDate && exitDate.getMonth() === month && exitDate.getFullYear() === year) {
            effectiveEnd = exitDate;
            isProRated = true;
            joinerType = joinerType === 'joiner' ? 'joiner-exit' : 'exit';
        }

        // Calculate effective payable days (total calendar days in effective period)
        let effectiveDays = totalPayableDays;
        let effectiveHolidayCount = data.holidayCount || 0;
        let effectiveWeeklyOffCount = data.weeklyOffCount || 0;

        if (isProRated) {
            const effectiveData = calculateWorkingDaysBetween(effectiveStart, effectiveEnd);
            effectiveDays = effectiveData.totalDays;
            effectiveHolidayCount = effectiveData.holidayCount;
            effectiveWeeklyOffCount = effectiveData.weeklyOffCount;
        }

        const prorationFactor = totalPayableDays > 0 ? effectiveDays / totalPayableDays : 1;

        return {
            isProRated,
            joinerType,
            joiningDate,
            exitDate,
            effectiveStart,
            effectiveEnd,
            effectiveDays,
            effectiveHolidayCount,
            effectiveWeeklyOffCount,
            totalPayableDays,
            prorationFactor: Math.round(prorationFactor * 100) / 100
        };
    }, [month, year, calculateWorkingDays, calculateWorkingDaysBetween]);

    // Get month working days data
    const monthData = useMemo(() => {
        return calculateWorkingDays();
    }, [calculateWorkingDays]);

    return {
        // Data
        holidays,
        weeklyOffPolicy,
        monthData,
        month,
        year,
        loading,
        error,

        // Functions
        calculateWorkingDays,
        calculateWorkingDaysBetween,
        calculateLOP,
        calculateProRata,
        getWeeklyOffDays,

        // Refresh
        refetchHolidays: fetchHolidays
    };
};

export default useWorkingDays;
